import type { TypedDocumentNode } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';

import { availableBalancesFragment } from '@sorare/core/src/contexts/currentUser/queries';
import {
  Level,
  useSnackNotificationContext,
} from '@sorare/core/src/contexts/snackNotification';
import { formatGqlErrors } from '@sorare/core/src/lib/gql';

import type {
  CancelOfferMutation,
  CancelOfferMutationVariables,
} from './__generated__/useCancelOffer.graphql';

const CANCEL_OFFER_MUTATION = gql`
  mutation CancelOfferMutation($input: cancelOfferInput!) {
    cancelOffer(input: $input) {
      tokenOffer {
        id
        status
        cancelledAt
        sender {
          ... on User {
            slug
          }
          ... on AnonymousUser {
            id
          }
        }
        senderSide {
          id
          cards: anyCards {
            slug
            liveSingleSaleOffer {
              id
              status
            }
            myMintedSingleSaleOffer {
              id
            }
          }
        }
        receiverSide {
          id
          cards: anyCards {
            slug
            liveSingleBuyOffers {
              id
            }
          }
        }
      }
      currentUser {
        slug
        ...CurrentUserProvider_availableBalances
      }
      errors {
        path
        message
        code
      }
    }
  }
  ${availableBalancesFragment}
` as TypedDocumentNode<CancelOfferMutation, CancelOfferMutationVariables>;

export const useCancelOffer = () => {
  const [cancelOffer] = useMutation(CANCEL_OFFER_MUTATION);
  const { showNotification } = useSnackNotificationContext();

  return async (blockchainId: string) => {
    const result = await cancelOffer({
      variables: {
        input: {
          blockchainId,
        },
      },
    });
    const errors = result.data?.cancelOffer?.errors || [];
    if (errors.length) {
      showNotification(
        'errors',
        { errors: formatGqlErrors(errors) },
        { level: Level.WARN }
      );
      return errors;
    }
    return null;
  };
};
